import { useEffect, useState } from "react";

/** True when the user has asked for reduced motion. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/**
 * Drives an indeterminate 0 -> 100 loop for the branded loader.
 * Returns 100 immediately when reduced motion is preferred so the completed
 * lockup is shown (only the bar keeps moving in that case).
 */
export function useLoopProgress(durationMs = 2500, enabled = true): number {
  const reduced = usePrefersReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    let start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= durationMs) start = now;
      setProgress(Math.min(100, ((elapsed % durationMs) / durationMs) * 100));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [durationMs, enabled]);

  return reduced ? 100 : progress;
}
